import { useAtomValue } from 'jotai'
import {
    ArrowUpRight,
    Facebook,
    Instagram,
    Linkedin,
    PhoneCall,
    Twitter,
    Youtube,
} from 'lucide-react'
import { regionAtom } from '~/lib/jotai/region'
import { Regions } from '~/models/region'

export function Footer() {
    const region = useAtomValue(regionAtom)

    const socialMedia = {
        [Regions.Barbados.code]: [
            {
                id: 'facebook',
                url: 'https://www.facebook.com/bcicbarbados/',
                icon: <Facebook />,
            },
            {
                id: 'instagram',
                url: 'https://www.instagram.com/bcicbarbados/',
                icon: <Instagram />,
            },
        ],
        [Regions.Jamaica.code]: [
            {
                id: 'facebook',
                url: 'https://www.facebook.com/BCICJamaica',
                icon: <Facebook />,
            },
            {
                id: 'twitter',
                url: 'https://x.com/bcicjamaica',
                icon: <Twitter />,
            },
            {
                id: 'linkedin',
                url: 'https://www.linkedin.com/company/british-caribbean-insurance-company-ltd./?originalSubdomain=jm',
                icon: <Linkedin />,
            },
            {
                id: 'youtube',
                url: 'https://www.youtube.com/channel/UC-sPV207BvWxYbOzJyVKxzA',
                icon: <Youtube />,
            },
            {
                id: 'instagram',
                url: 'https://www.instagram.com/bcicjamaica/',
                icon: <Instagram />,
            },
        ],
    }

    const externalLinks = {
        [Regions.Barbados.code]: [
            {
                label: 'The Claims Process',
                url: 'https://bciconline.com/ba/the-claims-process/',
            },
            {
                label: 'Claims FAQs',
                url: 'https://bciconline.com/ba/claims-faqs/',
            },
            {
                label: 'Roadside Assistance',
                url: 'https://bciconline.com/ba/roadside-assistance/',
            },
            {
                label: 'Find Broker',
                url: 'https://bciconline.com/ba/find-a-broker/',
            },
        ],
        [Regions.Jamaica.code]: [
            {
                label: 'The Claims Process',
                url: 'https://bciconline.com/the-claims-process/',
            },
            {
                label: 'Claims FAQs',
                url: 'https://bciconline.com/claims-faqs/',
            },
            {
                label: 'Roadside Assistance',
                url: 'https://bciconline.com/roadside-assistance/',
            },
            {
                label: 'Approved Assessors',
                url: 'https://bciconline.com/approved-assessors/',
            },
        ],
    }

    function groupObjects<T>(array: T[], size: number): T[][] {
        const result: T[][] = []

        for (let i = 0; i < array.length; i += size) {
            const group = array.slice(i, i + size)
            result.push(group)
        }

        return result
    }

    const groupedLinks = groupObjects(externalLinks[region], 3)

    return (
        <footer className='relative bottom-0 w-full text-sm bg-companyGray md:sticky'>
            <div className='container relative overflow-clip max-w-[3060px] py-8 px-10'>
                <div className='grid grid-cols-1 text-center md:text-left justify-center md:justify-between md:grid-cols-2 gap-8'>
                    <div className='space-y-4 z-[1]'>
                        <h1 className='font-black mt-11 tracking-tighter text-9xl leading-[2.5rem] mb-3'>
                            BCIC
                        </h1>

                        {region === Regions.Barbados.code ? (
                            <a
                                href='tel:+12466217340'
                                aria-label='Call us at 246 621 7340'
                                className='inline-flex gap-1 hover:font-bold hover:text-companyRed transition-all ease-in-out duration-300'
                                onFocus={() => {
                                    window.scrollTo({
                                        top: document.documentElement
                                            .scrollHeight,
                                        behavior: 'smooth',
                                    })
                                }}
                            >
                                <PhoneCall />
                                <span className='inline-flex items-stretch gap-[0.1rem]'>
                                    <ArrowUpRight size={16} />
                                    Call us at 246-621-7340
                                </span>
                            </a>
                        ) : (
                            <p
                                className='inline-flex items-center gap-1'
                                onFocus={() => {
                                    window.scrollTo({
                                        top: document.documentElement
                                            .scrollHeight,
                                        behavior: 'smooth',
                                    })
                                }}
                            >
                                <PhoneCall />
                                <span className='break-keep'>
                                    <a
                                        href='tel:+18769221260'
                                        className='hover:font-bold transition-all ease-in-out duration-300 text-companyBlack hover:text-companyRed'
                                    >
                                        <span className='inline-flex items-stretch gap-[0.1rem]'>
                                            <ArrowUpRight size={16} />
                                            Call us (876) 922-1260
                                        </span>
                                    </a>
                                    <span className='mx-1 inline-block bg-companyBlack h-4 w-[2px]'></span>
                                    <a
                                        href='tel:+18766182242'
                                        className='whitespace-nowrap hover:font-bold transition-all ease-in-out duration-300 text-companyBlack hover:text-companyRed'
                                    >
                                        <span className='inline-flex items-stretch gap-[0.1rem]'>
                                            <ArrowUpRight size={16} />
                                            (876) 618-2242
                                        </span>
                                    </a>
                                </span>
                            </p>
                        )}
                    </div>

                    <div className='z-[1] grid grid-cols-1 md:grid-cols-2 gap-3 md:justify-self-end'>
                        {groupedLinks.map((linkGroup, index) => (
                            <div className='space-y-4' key={`group${index}`}>
                                <ul className='space-y-2'>
                                    {linkGroup.map((link) => (
                                        <li key={link.label}>
                                            <a
                                                href={link.url}
                                                className='hover:text-companyRed inline-flex gap-1 items-center text-sm font-semibold transition-all duration-300 ease-in-out'
                                            >
                                                <ArrowUpRight />
                                                {link.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                <div className='mt-8 pt-8 border-t border-gray-300'>
                    <div className='flex flex-col md:flex-row justify-between items-center gap-4'>
                        <div className='z-[1] flex space-x-6'>
                            {socialMedia[region].map((social) => (
                                <a
                                    key={social.url}
                                    href={social.url}
                                    aria-label={social.id}
                                    className='bg-companyBlack focus:bg-companyRed text-white rounded-full p-2 hover:bg-companyRed hover:cursor-pointer hover:scale-110 transition-all ease-in-out'
                                >
                                    {social.icon}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>

                <div className='rounded-full absolute w-[50rem] h-[50rem] top-[45%] right-[-5%] bg-companyRed blur-2xl z-[0]'></div>
            </div>

            {region === Regions.Jamaica.code && (
                <div className='text-white text-sm px-4 py-7 text-center bg-companyBlack leading-tight'>
                    <p className='mb-3'>
                        BCIC is a member of IAJ and is licensed and regulated by
                        the Financial Services Commission in Jamaica.
                    </p>
                    <p>
                        To contact The Financial Services Commission: 39 - 43
                        Barbados Avenue, Kingston 5, Jamaica W.I. Telephone:
                        (876) 906-3010
                    </p>
                </div>
            )}
        </footer>
    )
}
